import { useMemo, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { ResetIcon } from '@dynatrace/strato-icons';

import { reduceExploreCancelSelect } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { isSameDomain } from '../../../core/utils/zoom.js';
import { useCurrentDomainState } from '../../hooks/store/selectors.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramInteractions } from '../../hooks/useHistogramInteractions.js';
import { useHistogramXScale } from '../../hooks/useHistogramXScale.js';
import { useResetInteractions } from '../../hooks/useResetInteractions.js';
import { HISTOGRAM_MESSAGES } from '../../messages/tooltip-text.js';

export const ResetButton = () => {
  const tabIndex = useA11yToolbarTabIndex('reset');
  const intl = useIntl();
  const { initialXScale } = useHistogramXScale();
  const setState = useSetState();

  const currentDomain = useCurrentDomainState();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoom, zoomX } = useHistogramInteractions();
  const hasZoomInteractions = zoom?.enabled || zoomX?.enabled;

  const resetInteractions = useResetInteractions();

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.code === 'Enter') {
      resetInteractions();
    }
  };

  const handleClick = () => {
    setState(reduceExploreCancelSelect);
    resetInteractions();
  };

  const isResettable = useMemo(
    () => !isSameDomain(currentDomain, initialXScale.domain()),
    [currentDomain, initialXScale],
  );

  const onlyXZoomIsEnabled = zoomX?.enabled && !zoom?.enabled;

  return hasZoomInteractions ? (
    <ChartToolbarRenderer.Control
      prefixIcon={<ResetIcon />}
      tooltipText={intl.formatMessage(HISTOGRAM_MESSAGES.resetMode)}
      tooltipKeyShortcut="R"
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      tabIndex={tabIndex}
      disabled={!isResettable}
      placement="toolbar"
      divider={onlyXZoomIsEnabled ? 'left' : undefined}
      showWhenCollapsed={isResettable}
    />
  ) : null;
};

ResetButton['displayName'] = 'ResetButton';
